import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { useNavigate, useLocation } from "react-router-dom";
import { NAV } from "../data/site";
import { Logo, scrollToId } from "./Logo";
import { useLead } from "../context/LeadContext";

// Nav items: section anchors + the Pricing route, in display order.
const ITEMS = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Portfolio", id: "portfolio" },
  { label: "Audit", id: "audit" },
  { label: "Contact", id: "contact" },
];

export const Header = () => {
  const { openLead } = useLead();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setMenu(false);
    if (pathname !== "/") navigate(`/#${id}`);
    else scrollToId(id);
  };
  const goRoute = (route) => { setMenu(false); navigate(route); };
  const handle = (item) => (item.route ? goRoute(item.route) : goSection(item.id));

  return (
    <motion.header
      initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${scrolled ? "border-b border-zinc-200/70 bg-white/80 backdrop-blur-xl" : "bg-transparent"}`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 md:px-10">
        <button onClick={() => (pathname !== "/" ? navigate("/") : goSection("home"))} data-testid="logo-link" aria-label="Jakin CDM home">
          <Logo />
        </button>

        <nav className="hidden items-center gap-7 lg:flex">
          {ITEMS.map((n) => (
            <button
              key={n.label} onClick={() => handle(n)} data-testid={`nav-${(n.id || "pricing")}-link`}
              className="group relative font-sans text-sm font-medium text-ink"
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-teal transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => openLead("consultation")} data-testid="header-book-button"
            className="hidden bg-teal px-5 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-ink sm:block"
          >
            Book a Consultation
          </button>
          <button onClick={() => setMenu((m) => !m)} data-testid="mobile-menu-toggle" className="lg:hidden" aria-label="Menu">
            {menu ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menu && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-zinc-200 bg-white lg:hidden" data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {ITEMS.map((n) => (
                <button key={n.label} onClick={() => handle(n)} className="py-3 text-left font-display text-2xl font-semibold tracking-tight text-ink">
                  {n.label}
                </button>
              ))}
              <button onClick={() => { openLead("consultation"); setMenu(false); }} className="mt-2 bg-teal py-4 text-sm font-bold uppercase tracking-wider text-white">
                Book a Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
